import { Schema } from 'effect';

export const FunnelReason = Schema.Literals([
  'faster',
  'hard',
  'eyes',
  'focus',
]);
export type FunnelReason = typeof FunnelReason.Type;

export const FunnelSegment = Schema.Literals([
  'study',
  'articles',
  'work',
  'else',
]);
export type FunnelSegment = typeof FunnelSegment.Type;

export const FunnelDailyGoal = Schema.Literals(['15', '30', '60']);
export type FunnelDailyGoal = typeof FunnelDailyGoal.Type;

export const FunnelFlow = Schema.Literals(['signup-first', 'paywall-first']);
export type FunnelFlow = typeof FunnelFlow.Type;
export const DEFAULT_FUNNEL_FLOW: FunnelFlow = 'signup-first';

export const FunnelStaticStep = Schema.Literals([
  'welcome',
  'segment',
  'demo',
  'goals',
  'proof',
  'bring-anything',
  'daily-goal',
  'recap',
  'signup',
  'paywall',
  'account',
  'done',
]);
export type FunnelStaticStep = typeof FunnelStaticStep.Type;

export const FunnelBenefitStep = Schema.TemplateLiteral([
  'benefit-',
  FunnelReason,
]);
export type FunnelBenefitStep = typeof FunnelBenefitStep.Type;
export const FunnelStep = Schema.Union([FunnelStaticStep, FunnelBenefitStep]);
export type FunnelStep = typeof FunnelStep.Type;

export const StepSessionRule = Schema.Literals([
  'open',
  'anonymous',
  'authenticated',
]);
export type StepSessionRule = typeof StepSessionRule.Type;

// `welcome` is `anonymous`: signed-in visitors are redirected to the app at
// the funnel entrance. The route-level gate is the only guard for the
// homepage `homepage-cta-funnel-v1` treatment CTAs — the homepage is a
// cdn-shared cached route, so its links cannot check the session themselves.
export function stepSessionRule(
  step: FunnelStep,
  flow: FunnelFlow = DEFAULT_FUNNEL_FLOW,
): StepSessionRule {
  if (step === 'welcome') return 'anonymous';
  if (step === 'paywall') {
    return flow === 'paywall-first' ? 'open' : 'authenticated';
  }
  return 'open';
}

export const isFunnelReason = Schema.is(FunnelReason);
export const isFunnelSegment = Schema.is(FunnelSegment);
export const isFunnelDailyGoal = Schema.is(FunnelDailyGoal);

export const isBenefitStep = Schema.is(FunnelBenefitStep);
export const isFunnelStep = Schema.is(FunnelStep);

export const FUNNEL_CAMPAIGN = 'academic';

export function expandSteps(
  reasons: ReadonlyArray<FunnelReason>,
  flow: FunnelFlow = DEFAULT_FUNNEL_FLOW,
): FunnelStep[] {
  const benefit =
    reasons.length > 0 ? reasons.map(benefitStep) : [benefitStep('faster')];
  const walkthrough: FunnelStep[] = [
    'welcome',
    'segment',
    'demo',
    'goals',
    ...benefit,
    'proof',
    'bring-anything',
    'daily-goal',
    'recap',
  ];
  return flow === 'paywall-first'
    ? [...walkthrough, 'paywall', 'account', 'done']
    : [...walkthrough, 'signup', 'paywall', 'done'];
}

function benefitStep(reason: FunnelReason): FunnelBenefitStep {
  return `benefit-${reason}`;
}

export function stepIndex(
  step: FunnelStep,
  reasons: ReadonlyArray<FunnelReason>,
  flow: FunnelFlow = DEFAULT_FUNNEL_FLOW,
): number {
  return expandSteps(reasons, flow).indexOf(step);
}

export function nextStep(
  step: FunnelStep,
  reasons: ReadonlyArray<FunnelReason>,
  flow: FunnelFlow = DEFAULT_FUNNEL_FLOW,
): FunnelStep | undefined {
  const steps = expandSteps(reasons, flow);
  const i = steps.indexOf(step);
  if (i === -1) {
    return isBenefitStep(step) ? 'proof' : undefined;
  }
  return steps[i + 1];
}

export function prevStep(
  step: FunnelStep,
  reasons: ReadonlyArray<FunnelReason>,
  flow: FunnelFlow = DEFAULT_FUNNEL_FLOW,
): FunnelStep | undefined {
  const steps = expandSteps(reasons, flow);
  const i = steps.indexOf(step);
  if (i === -1) {
    return isBenefitStep(step) ? 'goals' : undefined;
  }
  return i > 0 ? steps[i - 1] : undefined;
}
